import { useState, useEffect, useCallback } from 'react';
// @ts-ignore - JS module without types
import { NetworkService } from '../services/networkService.js';

export interface NetworkInfo {
  isOnline: boolean;
  effectiveType: string;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(NetworkService.isOnline);
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>(NetworkService.getNetworkInfo());
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const cleanup = NetworkService.addNetworkListener((status: string, online: boolean) => {
      setIsOnline(online);
      setNetworkInfo(NetworkService.getNetworkInfo());
      
      if (online && isConnecting) {
        setIsConnecting(false);
      }
    });

    return cleanup;
  }, [isConnecting]);

  const testConnectivity = useCallback(async () => {
    try {
      const isConnected = await NetworkService.testConnectivity();
      return isConnected;
    } catch (error) {
      console.error('Connectivity test failed:', error);
      return false;
    }
  }, []);

  const waitForOnline = useCallback(async (timeout = 30000) => {
    if (isOnline) return true;
    
    setIsConnecting(true);
    
    try {
      await NetworkService.waitForOnline(timeout);
      return true;
    } catch (error) {
      console.error('Failed to wait for online status:', error);
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [isOnline]);

  const isSlowConnection = useCallback(() => {
    const connection = networkInfo;
    return (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g' ||
      (connection.downlink !== null && connection.downlink < 1) ||
      (connection.rtt !== null && connection.rtt > 2000)
    );
  }, [networkInfo]);

  return {
    isOnline,
    networkInfo,
    isConnecting,
    testConnectivity,
    waitForOnline,
    isSlowConnection
  };
}